import axios, { AxiosError, AxiosInstance, AxiosResponse, Method } from 'axios'
import { AuthenticationService } from '..'
import { ICognitoUserSession } from '../../models'
import { appsDocumentSelected } from '../../helpers/app'


const apiUrl = `${process.env.REACT_APP_API_URL}/`

interface IHeaders {
  [key: string]: string
}

export abstract class BaseHttpService {
  public axiosInstance: AxiosInstance
  private boundResponse = this.handleResponse.bind(this)
  private boundResponseError = this.handleResponseError.bind(this)

  constructor() {
    this.axiosInstance = axios.create({ baseURL: apiUrl })
    this.axiosInstance.interceptors.response.use(this.boundResponse, this.boundResponseError)
  }

  public async get(segment: string, data?: any): Promise<any> {
    return this.buildRequest(segment, 'GET', null, data)
  }

  public async post(segment: string, body?: any, data?: any): Promise<any> {
    return this.buildRequest(segment, 'POST', body, data)
  }

  public async patch(segment: string, body: any): Promise<any> {
    return this.buildRequest(segment, 'PATCH', body)
  }

  public async put(segment: string, body?: any): Promise<any> {
    return this.buildRequest(segment, 'PUT', body)
  }

  public async delete(segment: string, body?: any): Promise<any> {
    return this.buildRequest(segment, 'DELETE', body)
  }

  private async buildRequest(segment: string, method: Method = 'GET', body?: any, data?: any): Promise<any> {
    const headers: IHeaders = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-BOD-APP-CODE': (data && (data.token || data.appCode) ? process.env.REACT_APP_APP_CODE_E_CORRIDOR : process.env.REACT_APP_appCode) || 'AMB',
      // 'X-BOD-REQUIRE-DOCUMENTS': appsDocumentSelected?.requireDocumentation ? 'true' : 'false',

      // UNCOMMENT FOR LOCAL TESTING & SET PROPER VALUES
      // 'Access-Control-Allow-Origin': '*',
      // 'X-BOD-USER-ID': '180707787',
      // 'X-BOD-AUTHORIZED-CLIENT-CODES': 'DIVERMOVE'
    }

    const token = await this.getToken();
    if (token) headers.Authorization = `Bearer ${token}`;

    switch (method) {
      case 'PUT':
        return this.axiosInstance.put(segment, body, { headers })
      case 'POST':
        return this.axiosInstance.post(segment, body, { headers })
      case 'DELETE':
        // axios only sends a DELETE body when it travels in `data`
        return this.axiosInstance.delete(segment, { headers, data: body })
      case 'PATCH':
        return this.axiosInstance.patch(segment, body, { headers })
      default:
        return this.axiosInstance.get(segment, { headers })
    }
  }

  private handleResponse(response: any): AxiosResponse<any> {
    return response.data
  }

  private handleResponseError(axiosError: AxiosError): AxiosError | any {
    throw axiosError.response;
  }

  private async getToken(): Promise<string> {
    let tokens: ICognitoUserSession | null

    try {
      tokens = await AuthenticationService.getTokens()
    } catch (error) {
      tokens = null
    }

    if (!tokens || !tokens.idToken) return ''
    return tokens.idToken.toString()
  }
}
